import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSales } from "@/contexts/SalesContext";
import useAEPData from "@/pages/aep/useAEPData";
import AEPSetupModal from "@/pages/aep/AEPExportWizard";
import SignaturesModal from "@/pages/aep/SignaturesModal";
import {
  SECTIONS, ORIENT_CLASS, MARGIN_CLASS, FONT_CLASS, MARGIN_MM,
} from "@/pages/aep/shared";
import {
  AEPCover, AEPSummary, AEPDashboard, AEPControleDoc, AEPResumoExec,
  AEPPrioridades, AEPRastreabilidade, AEPHeatmap, AEPTimeline,
  AEPChecklist, AEPLgpd, AEPIntro, AEPBase, AEPConsolidacao,
  AEPMatriz, AEPPlano, AEPPlanoAnual, AEPAbsenteismo, AEPRecomendacoesIA,
  AEPConclusao, AEPAssinaturas,
} from "@/pages/aep/AEPSections";

export default function AEPReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const { whatsappUrl, mailtoUrl } = useSales();
  const isTrial = Boolean(user?.is_trial);

  // Page setup state
  const [showSetup, setShowSetup] = useState(false);
  const [showSignatures, setShowSignatures] = useState(false);
  // Bumped after signatures save so AEPSections re-reads the assessment doc.
  const [signRefreshKey, setSignRefreshKey] = useState(0);

  const { data, actions, responses, derived, timelineBuckets } = useAEPData(id, signRefreshKey);
  const [orient, setOrient] = useState("retrato");
  const [margin, setMargin] = useState("normal");
  const [font, setFont]     = useState("normal");
  const [included, setIncluded] = useState(
    () => Object.fromEntries(SECTIONS.map((s) => [s.k, true])),
  );

  // Inject @page orientation/margin into a <style> tag for print
  useEffect(() => {
    const id_ = "aep-page-style";
    let style = document.getElementById(id_);
    if (!style) {
      style = document.createElement("style");
      style.id = id_;
      document.head.appendChild(style);
    }
    // textContent (not innerHTML) — CSS doesn't need HTML parsing.
    style.textContent = `@page { size: A4 ${orient}; margin: ${MARGIN_MM[margin]}; }`;
  }, [orient, margin]);

  // Auto-print when loaded with ?autoprint=1
  useEffect(() => {
    if (!data) return;
    if (isTrial) return; // Trial users cannot autoprint.
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoprint") !== "1") return;
    const t = setTimeout(() => {
      try { window.print(); }
      catch (err) { toast.error("Falha ao abrir impressão: " + err.message); }
    }, 1200);
    return () => clearTimeout(t);
  }, [data, isTrial]);

  const triggerPrint = () => {
    if (isTrial) {
      toast.error("Geração de PDF disponível apenas no plano completo. Veja os planos para assinar agora.");
      // Record the paywall hit as a lead. Best-effort, ignore failures.
      api.post("/leads/upgrade-click", {
        event_type: "paywall_hit",
        context: `AEP print attempt · assessment ${id}`,
      }).catch(() => {});
      setTimeout(() => { window.location.href = "/app/planos"; }, 1500);
      return;
    }
    setShowSetup(false);
    setTimeout(() => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("autoprint", "1");
        const w = window.open(url.toString(), "_blank");
        if (!w) {
          window.focus(); window.print();
          toast.info("Se o pop-up foi bloqueado, libere ou use Ctrl+P / Cmd+P.");
        }
      } catch {
        toast.error("Não foi possível abrir o diálogo. Use Ctrl+P / Cmd+P.");
      }
    }, 250);
  };

  if (!data || !derived) {
    return <div className="p-12 text-slate-500">Carregando…</div>;
  }

  const show = (k) => included[k];
  const { a, company, date, hash, score100, criticalCount, ranked, focoDim, critByDim, maxResp } = derived;

  return (
    <div className="bg-white min-h-screen">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-50 bg-slate-900 text-white px-6 py-3 flex justify-between items-center gap-3 flex-wrap">
        <Link to={`/app/avaliacoes/${id}`} className="text-sm" data-testid="aep-back-link">← Voltar</Link>
        <div className="text-sm">
          Relatório AEP · {orient === "paisagem" ? "Paisagem" : "Retrato"} ·{" "}
          {SECTIONS.filter((s) => included[s.k]).length}/{SECTIONS.length} seções
        </div>
        <div className="flex gap-2">
          {isTrial ? (
            <>
              <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-400/40 text-amber-200 text-xs font-bold uppercase tracking-wider"
                    data-testid="aep-trial-badge">
                <i className="fa-solid fa-lock"></i> Modo demonstração
              </span>
              <Link to="/app/planos"
                 onClick={() => { api.post("/leads/upgrade-click", { event_type: "paywall_hit", context: `AEP toolbar · assessment ${id}` }).catch(() => {}); }}
                 className="btn bg-[#8BC53F] text-emerald-950 hover:bg-white text-sm"
                 data-testid="aep-upgrade-button">
                <i className="fa-solid fa-tags"></i> Ver planos e assinar
              </Link>
            </>
          ) : (
            <>
              <button onClick={() => setShowSignatures(true)} className="btn btn-secondary text-sm" data-testid="aep-sign-button">
                <i className="fa-solid fa-signature"></i> Assinar
              </button>
              <button onClick={() => setShowSetup(true)} className="btn btn-primary text-sm" data-testid="aep-setup-button">
                <i className="fa-solid fa-wand-magic-sparkles"></i> Gerar AEP em PDF
              </button>
              <button onClick={triggerPrint} className="btn btn-secondary text-sm" data-testid="aep-print-button">
                <i className="fa-solid fa-print"></i> Imprimir rápido
              </button>
            </>
          )}
        </div>
      </div>

      {isTrial && (
        <div className="no-print bg-amber-50 border-b border-amber-200 px-6 py-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3" data-testid="aep-trial-banner">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-circle-info"></i>
            <span>Você está no <strong>plano Demonstração</strong>. A pré-visualização do relatório AEP está liberada, mas a geração/impressão/salvamento em PDF está disponível apenas no plano completo.</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to="/app/planos"
                  onClick={() => { api.post("/leads/upgrade-click", { event_type: "paywall_hit", context: `AEP banner · assessment ${id}` }).catch(() => {}); }}
                  className="btn btn-primary text-xs" data-testid="aep-trial-plans">
              <i className="fa-solid fa-tags"></i> Ver planos
            </Link>
            <a href={mailtoUrl()}
               onClick={() => { api.post("/leads/upgrade-click", { event_type: "upgrade_email", context: `AEP banner · assessment ${id}` }).catch(() => {}); }}
               className="btn btn-ghost text-xs" data-testid="aep-trial-email">
              <i className="fa-solid fa-envelope"></i> E-mail
            </a>
            <a href={whatsappUrl()} target="_blank" rel="noreferrer"
               onClick={() => { api.post("/leads/upgrade-click", { event_type: "upgrade_whatsapp", context: `AEP banner · assessment ${id}` }).catch(() => {}); }}
               className="btn btn-ghost text-xs" data-testid="aep-trial-whatsapp">
              <i className="fa-brands fa-whatsapp"></i> WhatsApp
            </a>
          </div>
        </div>
      )}

      <div className={`mx-auto aep-body ${ORIENT_CLASS[orient]} ${MARGIN_CLASS[margin]} ${FONT_CLASS[font]}`} lang="pt-BR">
        {show("capa")            && <AEPCover            a={a} company={company} date={date} hash={hash} />}
        {show("sumario")         && <AEPSummary          included={included} />}
        {show("dashboard")       && <AEPDashboard        data={data} score100={score100} criticalCount={criticalCount} />}
        {show("controle_doc")    && <AEPControleDoc      a={a} company={company} date={date} hash={hash} />}
        {show("resumo")          && <AEPResumoExec       data={data} score100={score100} criticalCount={criticalCount} focoDim={focoDim} />}
        {show("prioridades")     && <AEPPrioridades      ranked={ranked} />}
        {show("rastreabilidade") && <AEPRastreabilidade  ranked={ranked} cnpj={company?.cnpj} />}
        {show("heatmap")         && <AEPHeatmap          ranked={ranked} critByDim={critByDim} maxResp={maxResp} />}
        {show("timeline")        && <AEPTimeline         timelineBuckets={timelineBuckets} />}
        {show("checklist")       && <AEPChecklist        hasActions={actions.length > 0} />}
        {show("lgpd")            && <AEPLgpd />}
        {show("intro")           && <AEPIntro />}
        {show("base")            && <AEPBase             responses={responses} totalQuestions={data?.questions?.length} />}
        {show("consolidacao")    && <AEPConsolidacao     data={data} />}
        {show("matriz")          && <AEPMatriz           data={data} criticalCount={criticalCount} matrixType={a.matrix_type || "pxs"} expectedWorkers={a.n_trabalhadores || 0} />}
        {show("plano")           && <AEPPlano            actions={actions} />}
        {show("recomendacoes_ia") && <AEPRecomendacoesIA  assessmentId={id} assessmentIds={[id].filter(Boolean)} />}
        {show("plano_anual")     && <AEPPlanoAnual       companyId={company?.id || a.company_id} />}
        {show("absenteismo")     && <AEPAbsenteismo      companyId={company?.id || a.company_id} />}
        {show("conclusao")       && <AEPConclusao        data={data} score100={score100} criticalCount={criticalCount} />}
        {show("assinaturas")     && <AEPAssinaturas      a={a} company={company} date={date} hash={hash} />}
      </div>

      {showSetup && (
        <AEPSetupModal
          orient={orient}     setOrient={setOrient}
          margin={margin}     setMargin={setMargin}
          font={font}         setFont={setFont}
          included={included} setIncluded={setIncluded}
          onClose={() => setShowSetup(false)}
          onPrint={triggerPrint}
        />
      )}

      {showSignatures && (
        <SignaturesModal
          assessmentId={id}
          initial={a}
          onClose={() => setShowSignatures(false)}
          onSaved={() => { setShowSignatures(false); setSignRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
